// ============================================================
// datetime — 前端日期工具（唯一真實來源）
// ------------------------------------------------------------
// 【為什麼有這個檔】
// 本專案反覆發生同一個 bug：用 toISOString().slice(0,10) 取「今天」。
// 那是 UTC 日期，台北早上 08:00 之前會變成前一天。已知災情：
//   - 5/11 排班抓到前一天的例假日，員工被算成「遲到 479 分鐘」
//   - 員工首頁早上顯示前一天的班表與打卡狀態
//   - 每月 1 號早上打卡 QR 頁開在上個月
//
// 修法在 ClockInPage 內聯過一次，但其他 5 個地方沒跟著改。
// 一律改用本檔，不要再自己寫日期轉換。
// ============================================================

const TAIPEI_TZ = 'Asia/Taipei';

/** 把任意時刻轉成台北日曆日字串 YYYY-MM-DD */
export function toTaipeiDateStr(d: Date): string {
  // en-CA 產生 ISO 8601 的 YYYY-MM-DD
  return d.toLocaleDateString('en-CA', { timeZone: TAIPEI_TZ });
}

/** 台北的今天 YYYY-MM-DD */
export function taipeiToday(): string {
  return toTaipeiDateStr(new Date());
}

/** 台北的當月 YYYY-MM */
export function taipeiThisMonth(): string {
  return taipeiToday().slice(0, 7);
}

/**
 * 台北本週的週一（YYYY-MM-DD）。
 * 用 UTC 曆法做純日期加減，避免裝置本地時區介入。
 */
export function taipeiWeekStart(dateStr?: string): string {
  const base = dateStr ?? taipeiToday();
  const [y, m, d] = base.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dow = dt.getUTCDay();                 // 0 = 週日
  dt.setUTCDate(dt.getUTCDate() + (dow === 0 ? -6 : 1 - dow));
  return dt.toISOString().slice(0, 10);
}

/** 月份字串加減：'2026-08' + 1 → '2026-09' */
export function shiftMonth(monthStr: string, delta: number): string {
  const [y, m] = monthStr.split('-').map(Number);
  const total = y * 12 + (m - 1) + delta;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

/** ISO 時刻 → 台北 HH:MM（給打卡時間顯示用） */
export function formatTaipeiTime(iso: string | null | undefined): string {
  if (!iso) return '--:--';
  return new Date(iso).toLocaleTimeString('en-GB', {
    timeZone: TAIPEI_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** 星期名稱（0 = 週日） */
export const WEEKDAY_LABELS = ['日', '一', '二', '三', '四', '五', '六'] as const;

/** 取台北日期字串的星期名稱 */
export function taipeiWeekdayLabel(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return WEEKDAY_LABELS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
}
